<script>
import { mapActions } from 'vuex';
import request from '@/services/request';
import Catalogue from '@/services/catalogue';

export default {
    props: {
        prop_edit: {
            default: true,
        },
    },

    data() {
        return {
            catalogue: new Catalogue(),
            errors: {},
            send: false,
            load: false,
            amount: 0,
            contract: {
                contract_id: 0,
                id: 0,
                currency_id: 0,
            },
            bills: [],
            billsData: [],
        };
    },

    computed: {
        invoiceCurrencies() {
            return (this.catalogue.currencies || []).filter(currency =>
                [1, 2].indexOf(Number(currency.id)) !== -1
            );
        },
    },

    methods: {
        /* =========================================================
         * MONEDA / TIPO DE CAMBIO
         * ========================================================= */
        isUsdContract() {
            return Number(this.contract.currency_id) === 2;
        },

        contractCurrencyCode() {
            return this.isUsdContract() ? 'USD' : 'MXN';
        },

        normalizeInvoiceCurrency(currencyId) {
            // Si el contrato está en MXN, la factura permanece en MXN.
            if (!this.isUsdContract()) {
                return 1;
            }

            // Si el contrato está en USD, permitimos USD o MXN.
            return Number(currencyId) === 1 ? 1 : 2;
        },

        billCurrencyCode(bill) {
            return Number(bill && bill.currency_id) === 2 ? 'USD' : 'MXN';
        },

        requiresExchangeRate(bill) {
            // Todo contrato en USD debe capturar tipo de cambio,
            // sin importar si la factura se emite en USD o MXN.
            return this.isUsdContract() && !!bill;
        },

        requiresCurrencyConversion(bill) {
            // La multiplicación solo aplica cuando:
            // contrato USD -> factura MXN.
            return this.isUsdContract() && Number(bill && bill.currency_id) === 1;
        },

        onBillCurrencyChange(bill) {
            // Solo normalizamos la moneda. NO borramos el tipo de cambio,
            // porque los contratos USD siempre deben capturarlo.
            bill.currency_id = this.normalizeInvoiceCurrency(bill.currency_id);
        },

        getBillFromItem(item) {
            if (!item || item.bill_id === null || item.bill_id === undefined) {
                return null;
            }

            return this.billsData[Number(item.bill_id)] || null;
        },

        getItemInvoiceCurrency(item) {
            const bill = this.getBillFromItem(item);
            return bill ? this.billCurrencyCode(bill) : this.contractCurrencyCode();
        },

        getExchangeRate(item) {
            const bill = this.getBillFromItem(item);

            // El tipo de cambio puede existir en USD -> USD,
            // pero SOLO se usa como multiplicador en USD -> MXN.
            if (!this.requiresCurrencyConversion(bill)) {
                return 1;
            }

            return parseFloat(bill.exchange_rate_bill) || 0;
        },

        hasValidExchangeRate(item) {
            const bill = this.getBillFromItem(item);

            if (!this.requiresExchangeRate(bill)) {
                return true;
            }

            return (parseFloat(bill.exchange_rate_bill) || 0) > 0;
        },

        getConvertedAmount(item) {
            const amount = parseFloat(item.final_amount) || 0;
            return amount * this.getExchangeRate(item);
        },

        getConvertedDiscount(item) {
            const discount = parseFloat(item.discount) || 0;
            return discount * this.getExchangeRate(item);
        },

        billHasItems(billIndex) {
            return this.bills.some(item => Number(item.bill_id) === Number(billIndex));
        },

        /* =========================================================
         * IMPUESTOS / TOTALES
         * ========================================================= */
        onChangeImpuesto(itemBase, tipo) {
            const factura = this.billsData[itemBase.bill_id];

            if (factura && Number(factura.type) === 1) {
                this.bills.forEach(item => {
                    if (Number(item.bill_id) === Number(itemBase.bill_id)) {
                        item[tipo] = itemBase[tipo];
                    }
                });
            }
        },

        calcularTotales(item) {
            // final_amount y discount se conservan en la moneda ORIGINAL
            // del contrato. Solo convertimos para cálculo/visualización.
            const montoBase = this.getConvertedAmount(item);
            const descuento = this.getConvertedDiscount(item);
            const subtotal = Math.max(0, montoBase - descuento);

            // 1. IEPS sobre subtotal
            const iepsRate = 0.08;
            const ieps = item.ieps ? subtotal * iepsRate : 0;

            // 2. IEPS forma parte de la base de IVA/retenciones
            const baseImpuestos = subtotal + ieps;

            // 3. IVA
            const ivaRate = 0.16;
            const iva = item.iva ? baseImpuestos * ivaRate : 0;

            // 4. ISR retenido
            const isrRetRate = 0.10;
            const isrRet = item.isr_ret ? baseImpuestos * isrRetRate : 0;

            // 5. IVA retenido
            const ivaRetRate = 0.106667;
            const ivaRet = item.iva_ret ? baseImpuestos * ivaRetRate : 0;

            // 6. Total
            const total = subtotal + ieps + iva - isrRet - ivaRet;

            return {
                montoBase,
                descuento,
                subtotal,
                baseImpuestos,
                iva,
                ieps,
                isrRet,
                ivaRet,
                total,
            };
        },

        /* =========================================================
         * FACTURAS / CONCEPTOS
         * ========================================================= */
        addItem() {
            this.bills.push({
                id: '',
                description: '',
                bill_id: 0,
                final_amount: 0,
                discount: 0,
                iva: 0,
                ieps: 0,
                isr_ret: 0,
                iva_ret: 0,
                total: 0,
            });
        },

        addBillData(data = null) {
            const companies = this.catalogue.companies_buildings || [];
            const defaultBillingDataId = companies.length ? companies[0].id : null;

            const defaultData = {
                name: 'Factura ' + (this.billsData.length + 1),
                billing_data_id: defaultBillingDataId,
                type: 1,
                contract_dues_id: this.contract.id,
                currency_id: this.contract.currency_id,
                exchange_rate_bill: 0,
                payment_method: 'PUE',
            };

            const bill = Object.assign({}, defaultData, data || {});

            bill.currency_id = this.normalizeInvoiceCurrency(bill.currency_id);

            // Si el contrato está en MXN no utilizamos tipo de cambio.
            if (!this.isUsdContract()) {
                bill.exchange_rate_bill = 0;
            }

            this.billsData.push(bill);
        },

        /* =========================================================
         * GUARDADO
         * ========================================================= */
        validateBeforeSave() {
            this.errors = {};

            const invalidExchangeIndex = this.billsData.findIndex((bill, index) => {
                if (!this.billHasItems(index)) {
                    return false;
                }

                return this.requiresExchangeRate(bill) &&
                    (parseFloat(bill.exchange_rate_bill) || 0) <= 0;
            });

            if (invalidExchangeIndex !== -1) {
                this.errors = {
                    exchange_rate_bill: [
                        'Debes indicar el tipo de cambio para las facturas de contratos en USD.',
                    ],
                };

                return false;
            }

            return true;
        },

        async triggerSave() {
            if (!this.validateBeforeSave()) {
                return;
            }

            this.send = true;

            const data_form = new FormData();
            data_form.append('contract', JSON.stringify(this.contract));
            data_form.append('bills', JSON.stringify(this.bills));
            data_form.append('bills_data', JSON.stringify(this.billsData));

            const endpoint = '/external_bill';

            try {
                const { data } = await request.post(endpoint, data_form);

                if (data) {
                    this.successNotification(data.message);
                    this.closedModal();
                    Event.$emit('event-load-contract_dues');
                }
            } catch (error) {
                this.errors = {};

                if (error.response && error.response.data) {
                    const outerMessage = error.response.data.message;

                    if (
                        typeof outerMessage === 'string' &&
                        outerMessage.indexOf('Unable to create Invoice: ') !== -1
                    ) {
                        try {
                            const jsonString = outerMessage.split('Unable to create Invoice: ')[1];
                            const parsed = JSON.parse(jsonString);

                            if (parsed && parsed.message) {
                                alert(parsed.message);
                            }
                        } catch (parseError) {
                            // Si Facturapi cambia la estructura, dejamos que
                            // errorNotification muestre la respuesta original.
                        }
                    }

                    this.errors = await this.errorNotification(error);
                }
            } finally {
                this.send = false;
            }
        },

        /* =========================================================
         * MODAL
         * ========================================================= */
        closedModal() {
            this.load = false;
            this.errors = {};
            this.send = false;
            this.amount = 0;
            this.contract = {
                contract_id: 0,
                id: 0,
                currency_id: 0,
            };
            this.bills = [];
            this.billsData = [];
            this.$modal.hide('addCreateBillExternal');
        },

        async openModal(contract) {
            this.load = true;
            this.errors = {};
            this.send = false;
            this.bills = [];
            this.billsData = [];

            this.$modal.show('addCreateBillExternal');

            this.contract = {
                contract_id: contract.contract_id,
                id: contract.id,
                currency_id: Number(contract.contract.currency_id),
            };

            await this.catalogue.get_companies_buildings(contract.contract.building_id);

            const items = [];
            let bills = false;

            if (contract.group_type === 'extras') {
                contract.due_items_extras.forEach(_item => {
                    if (_item.item_bill == null) {
                        items.push({
                            id: _item.id,
                            description: _item.description,
                            bill_id: 0,
                            final_amount: _item.final_amount,
                            type: _item.type,
                            item_id: _item.item_id,
                            discount: 0,
                            iva: 0,
                            ieps: 0,
                            isr_ret: 0,
                            iva_ret: 0,
                            total: 0,
                        });
                    } else {
                        bills = true;
                    }
                });
            } else if (contract.group_type === 'rent') {
                contract.due_items_rent_only.forEach(_item => {
                    if (_item.item_bill == null) {
                        items.push({
                            id: _item.id,
                            description: _item.description,
                            bill_id: 0,
                            final_amount: _item.final_amount,
                            type: _item.type,
                            item_id: _item.item_id,
                            discount: 0,
                            iva: 0,
                            ieps: 0,
                            isr_ret: 0,
                            iva_ret: 0,
                            total: 0,
                        });
                    } else {
                        bills = true;
                    }
                });
            }

            if (
                !bills &&
                contract.contract.rules_billing &&
                contract.contract.rules_billing.length > 0 &&
                contract.group_type === 'rent'
            ) {
                contract.contract.rules_billing.forEach((rule, indexR) => {
                    this.addBillData({
                        name: rule.name,
                        billing_data_id: rule.billing_data_id,
                        type: parseInt(rule.type, 10),
                        contract_dues_id: contract.id,
                        currency_id: this.normalizeInvoiceCurrency(rule.currency_id),
                        exchange_rate_bill: 0,
                        payment_method: 'PUE',
                    });

                    if (rule.rules_items && rule.rules_items.length > 0) {
                        rule.rules_items.forEach(ruleItem => {
                            const idx = items.findIndex(it =>
                                it.item_id === ruleItem.item_id &&
                                it.type === parseInt(ruleItem.type, 10)
                            );

                            if (idx > -1) {
                                items[idx].bill_id = indexR;
                                items[idx].discount = ruleItem.discount;
                                items[idx].iva = ruleItem.iva;
                                items[idx].ieps = ruleItem.ieps;
                                items[idx].isr_ret = ruleItem.isr_ret;
                                items[idx].iva_ret = ruleItem.iva_ret;
                            }
                        });
                    }
                });
            } else {
                const companies = this.catalogue.companies_buildings || [];

                this.addBillData({
                    name: 'Factura principal',
                    billing_data_id: companies.length ? companies[0].id : null,
                    type: 1,
                    contract_dues_id: contract.id,
                    currency_id: this.contract.currency_id,
                    exchange_rate_bill: 0,
                    payment_method: 'PUE',
                });
            }

            this.bills = items;
            this.load = false;
        },

        ...mapActions({
            errorNotification: 'alert/error',
            successNotification: 'alert/success',
        }),
    },

    mounted() {
        this.catalogue.getCurrencies();
    },

    created() {
        Event.$on('event-show-addCreateBillExternals', this.openModal);
    },

    beforeDestroy() {
        Event.$off('event-show-addCreateBillExternals', this.openModal);
    },
};
</script>

<template>
    <modal
        name="addCreateBillExternal"
        :width="'92%'"
        :height="'auto'"
        :adaptive="true"
        :scrollable="true"
        :click-to-close="false"
    >
        <div class="billing-modal">
            <!-- Header -->
            <div class="billing-modal__header">
                <div class="billing-modal__title-group">
                    <div class="billing-modal__icon">
                        <i class="fas fa-file-invoice-dollar"></i>
                    </div>

                    <div>
                        <h4 class="billing-modal__title">Facturar</h4>
                        <p class="billing-modal__subtitle mb-0">
                            Configura las facturas, asigna los conceptos y valida los impuestos antes de generar.
                        </p>
                    </div>
                </div>

                <button
                    type="button"
                    class="billing-modal__close"
                    title="Cerrar"
                    aria-label="Cerrar"
                    @click="closedModal()"
                >
                    <i class="fas fa-times"></i>
                </button>
            </div>

            <!-- Loading -->
            <div v-if="load" class="billing-loading">
                <div class="billing-loading__spinner">
                    <i class="fas fa-circle-notch fa-spin"></i>
                </div>
                <h5 class="mb-1">Preparando información</h5>
                <p class="text-muted mb-0">
                    Estamos cargando los datos de facturación y conceptos.
                </p>
            </div>

            <template v-else>
                <div class="billing-modal__body">
                    <div class="row">
                        <!-- CONFIGURACIÓN DE FACTURAS -->
                        <div class="col-lg-4 mb-4 mb-lg-0">
                            <div class="billing-section-header">
                                <div class="billing-section-header__left">
                                    <div class="billing-section-header__number">1</div>
                                    <div>
                                        <h5 class="billing-section-header__title">
                                            Configuración de facturas
                                        </h5>
                                        <p class="billing-section-header__text">
                                            Define cómo se emitirá cada factura y sus datos fiscales.
                                        </p>
                                    </div>
                                </div>

                            </div>

                            <div class="invoice-list">
                                <div
                                    class="invoice-card"
                                    v-for="(bill, index) in billsData"
                                    :key="index"
                                >
                                    <div class="invoice-card__header">
                                        <div class="invoice-card__number">{{ index + 1 }}</div>
                                        <div class="invoice-card__heading">
                                            <strong>{{ bill.name || ('Factura ' + (index + 1)) }}</strong>
                                            <small>
                                                {{ bill.type == 1 ? 'Facturación integrada' : 'Facturación desglosada' }}
                                            </small>
                                        </div>
                                    </div>

                                    <div class="invoice-card__body">
                                        <div class="form-group">
                                            <label class="billing-label">Nombre de la factura</label>
                                            <input
                                                type="text"
                                                class="form-control billing-control"
                                                v-model="bill.name"
                                                placeholder="Ej. Factura principal"
                                            >
                                        </div>

                                        <div class="form-group">
                                            <label class="billing-label">Datos de facturación</label>
                                            <select
                                                class="form-control billing-control"
                                                v-model="bill.billing_data_id"
                                            >
                                                <option
                                                    :value="billingData.id"
                                                    v-for="(billingData, billingIndex) in catalogue.companies_buildings"
                                                    :key="billingIndex"
                                                >
                                                    {{ billingData.name }} - {{ billingData.rfc }}
                                                </option>
                                            </select>

                                            <div v-if="errors && errors.bills" class="billing-error">
                                                {{ errors.bills[0] }}
                                            </div>
                                        </div>

                                        <div class="form-group">
                                            <label class="billing-label">Tipo de facturación</label>
                                            <select
                                                class="form-control billing-control"
                                                v-model="bill.type"
                                            >
                                                <option value="1">Integrado</option>
                                                <option value="2">Desglosado</option>
                                            </select>

                                            <small class="billing-help" v-if="bill.type == 1">
                                                Los impuestos seleccionados se sincronizan entre los conceptos asignados a esta factura.
                                            </small>
                                            <small class="billing-help" v-else>
                                                Cada concepto puede manejar sus impuestos de forma independiente.
                                            </small>
                                        </div>

                                        <!-- Solo contratos USD pueden elegir USD/MXN -->
                                        <div class="form-group" v-if="isUsdContract()">
                                            <label class="billing-label">Moneda de la factura</label>
                                            <select
                                                class="form-control billing-control"
                                                v-model="bill.currency_id"
                                                @change="onBillCurrencyChange(bill)"
                                            >
                                                <option
                                                    :value="currency.id"
                                                    v-for="(currency, currencyIndex) in invoiceCurrencies"
                                                    :key="currencyIndex"
                                                >
                                                    {{ currency.name }}
                                                </option>
                                            </select>

                                            <small class="billing-help">
                                                El contrato está en USD. Puedes facturar en USD o convertir los conceptos a MXN.
                                            </small>
                                        </div>

                                        <!-- Todo contrato USD requiere tipo de cambio -->
                                        <div
                                            class="form-group exchange-panel"
                                            v-if="requiresExchangeRate(bill)"
                                        >
                                            <div class="exchange-panel__title">
                                                <i class="fas fa-exchange-alt"></i>
                                                <span v-if="requiresCurrencyConversion(bill)">
                                                    Conversión USD → MXN
                                                </span>
                                                <span v-else>
                                                    Tipo de cambio USD
                                                </span>
                                            </div>

                                            <label class="billing-label">Tipo de cambio</label>
                                            <money
                                                class="form-control billing-control"
                                                v-model="bill.exchange_rate_bill"
                                            ></money>

                                            <small
                                                class="billing-help"
                                                v-if="requiresCurrencyConversion(bill)"
                                            >
                                                Captura cuántos pesos mexicanos equivalen a 1 USD.
                                                Se utilizará para convertir los conceptos a MXN.
                                            </small>

                                            <small
                                                class="billing-help"
                                                v-else
                                            >
                                                La factura permanecerá en USD. El tipo de cambio se registra,
                                                pero no modifica los importes del concepto.
                                            </small>

                                            <div
                                                v-if="errors && errors.exchange_rate_bill"
                                                class="billing-error"
                                            >
                                                {{ errors.exchange_rate_bill[0] }}
                                            </div>
                                        </div>

                                        <div class="form-group mb-0">
                                            <label class="billing-label">Método de pago</label>
                                            <select
                                                class="form-control billing-control"
                                                v-model="bill.payment_method"
                                            >
                                                <option value="PUE">
                                                    PUE | Pago en una sola exhibición
                                                </option>
                                                <option value="PPD">
                                                    PPD | Pago en parcialidades o diferidos
                                                </option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <button
                                type="button"
                                class="btn billing-btn billing-btn--add w-100"
                                @click="addBillData()"
                            >
                                <i class="fas fa-plus mr-2"></i>
                                Agregar factura
                            </button>
                        </div>

                        <!-- CONCEPTOS -->
                        <div class="col-lg-8">
                            <div class="billing-section-header billing-section-header--items">
                                <div class="billing-section-header__left">
                                    <div class="billing-section-header__number">2</div>
                                    <div>
                                        <h5 class="billing-section-header__title">
                                            Conceptos a facturar
                                        </h5>
                                        <p class="billing-section-header__text">
                                            Asigna cada concepto a una factura, aplica descuentos e impuestos y revisa el total.
                                        </p>
                                    </div>
                                </div>

                            </div>

                            <div v-if="!bills.length" class="billing-empty-state">
                                <div class="billing-empty-state__icon">
                                    <i class="fas fa-receipt"></i>
                                </div>
                                <h5>No hay conceptos pendientes</h5>
                                <p class="mb-0">
                                    No encontramos conceptos disponibles para facturar en este movimiento.
                                </p>
                            </div>

                            <div
                                class="item-card"
                                v-for="(item, index) in bills"
                                :key="index"
                            >
                                <div class="item-card__header">
                                    <div class="item-card__title">
                                        <span class="item-card__index">{{ index + 1 }}</span>
                                        <div>
                                            <span class="item-card__label">Concepto</span>
                                            <strong v-if="item.id">{{ item.description }}</strong>
                                            <input
                                                v-else
                                                type="text"
                                                v-model="item.description"
                                                class="form-control billing-control mt-1"
                                                placeholder="Descripción del concepto"
                                            >
                                        </div>
                                    </div>

                                    <div class="item-card__amount" v-if="item.id">
                                        <span>Monto base</span>
                                        <strong>
                                            {{ item.final_amount | toCurrency }} {{ contractCurrencyCode() }}
                                        </strong>

                                        <small
                                            v-if="requiresCurrencyConversion(getBillFromItem(item)) && hasValidExchangeRate(item)"
                                            class="billing-conversion"
                                        >
                                            ≈ {{ getConvertedAmount(item) | toCurrency }} MXN
                                        </small>
                                    </div>
                                </div>

                                <div class="item-card__body">
                                    <div class="row">
                                        <div class="col-md-5">
                                            <div class="form-group">
                                                <label class="billing-label">Asignar a factura</label>
                                                <select
                                                    class="form-control billing-control"
                                                    v-model="item.bill_id"
                                                >
                                                    <option
                                                        :value="billIndex"
                                                        v-for="(bill, billIndex) in billsData"
                                                        :key="billIndex"
                                                    >
                                                        {{ bill.name }}
                                                    </option>
                                                </select>

                                                <div v-if="errors && errors.bills" class="billing-error">
                                                    {{ errors.bills[0] }}
                                                </div>
                                            </div>
                                        </div>

                                        <div class="col-md-3">
                                            <div class="form-group">
                                                <label class="billing-label">
                                                    Monto ({{ contractCurrencyCode() }})
                                                </label>

                                                <div
                                                    v-if="item.id"
                                                    class="billing-readonly-value billing-readonly-value--currency"
                                                >
                                                    <strong>
                                                        {{ item.final_amount | toCurrency }} {{ contractCurrencyCode() }}
                                                    </strong>

                                                    <small
                                                        v-if="requiresCurrencyConversion(getBillFromItem(item)) && hasValidExchangeRate(item)"
                                                    >
                                                        {{ getConvertedAmount(item) | toCurrency }} MXN
                                                    </small>
                                                </div>

                                                <money
                                                    v-else
                                                    class="form-control billing-control"
                                                    v-model="item.final_amount"
                                                ></money>
                                            </div>
                                        </div>

                                        <div class="col-md-4">
                                            <div class="form-group">
                                                <label class="billing-label">
                                                    Descuento ({{ contractCurrencyCode() }})
                                                </label>
                                                <money
                                                    class="form-control billing-control"
                                                    v-model="item.discount"
                                                ></money>

                                                <small
                                                    class="billing-help"
                                                    v-if="requiresCurrencyConversion(getBillFromItem(item))"
                                                >
                                                    El descuento se captura en USD y también se convierte a MXN.
                                                </small>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Vista explícita de la multiplicación -->
                                    <div
                                        class="conversion-preview"
                                        v-if="requiresCurrencyConversion(getBillFromItem(item))"
                                    >
                                        <div class="conversion-preview__icon">
                                            <i class="fas fa-calculator"></i>
                                        </div>

                                        <div class="conversion-preview__content">
                                            <strong>Conversión del concepto</strong>

                                            <template v-if="hasValidExchangeRate(item)">
                                                <span>
                                                    {{ item.final_amount | toCurrency }} USD
                                                    ×
                                                    {{ getExchangeRate(item) }}
                                                    =
                                                    <b>{{ getConvertedAmount(item) | toCurrency }} MXN</b>
                                                </span>
                                            </template>

                                            <span v-else class="conversion-preview__pending">
                                                Captura el tipo de cambio para calcular el importe en MXN.
                                            </span>
                                        </div>
                                    </div>

                                    <div class="tax-panel">
                                        <div class="tax-panel__header">
                                            <div>
                                                <strong>Impuestos y retenciones</strong>
                                                <small>
                                                    Activa únicamente los que correspondan a este concepto.
                                                </small>
                                            </div>
                                        </div>

                                        <div class="tax-options">
                                            <label
                                                class="tax-option"
                                                :class="{ 'tax-option--active': item.iva }"
                                            >
                                                <input
                                                    type="checkbox"
                                                    v-model="item.iva"
                                                    @change="onChangeImpuesto(item, 'iva')"
                                                >
                                                <span class="tax-option__check">
                                                    <i class="fas fa-check"></i>
                                                </span>
                                                <span>
                                                    <strong>IVA</strong>
                                                    <small>16%</small>
                                                </span>
                                            </label>

                                            <label
                                                class="tax-option"
                                                :class="{ 'tax-option--active': item.ieps }"
                                            >
                                                <input
                                                    type="checkbox"
                                                    v-model="item.ieps"
                                                    @change="onChangeImpuesto(item, 'ieps')"
                                                >
                                                <span class="tax-option__check">
                                                    <i class="fas fa-check"></i>
                                                </span>
                                                <span>
                                                    <strong>IEPS</strong>
                                                    <small>8%</small>
                                                </span>
                                            </label>

                                            <label
                                                class="tax-option"
                                                :class="{ 'tax-option--active': item.isr_ret }"
                                            >
                                                <input
                                                    type="checkbox"
                                                    v-model="item.isr_ret"
                                                    @change="onChangeImpuesto(item, 'isr_ret')"
                                                >
                                                <span class="tax-option__check">
                                                    <i class="fas fa-check"></i>
                                                </span>
                                                <span>
                                                    <strong>ISR retenido</strong>
                                                    <small>10%</small>
                                                </span>
                                            </label>

                                            <label
                                                class="tax-option"
                                                :class="{ 'tax-option--active': item.iva_ret }"
                                            >
                                                <input
                                                    type="checkbox"
                                                    v-model="item.iva_ret"
                                                    @change="onChangeImpuesto(item, 'iva_ret')"
                                                >
                                                <span class="tax-option__check">
                                                    <i class="fas fa-check"></i>
                                                </span>
                                                <span>
                                                    <strong>IVA retenido</strong>
                                                    <small>10.6667%</small>
                                                </span>
                                            </label>
                                        </div>
                                    </div>

                                    <div class="item-summary">
                                        <div class="item-summary__title">
                                            <i class="fas fa-calculator mr-2"></i>
                                            Resumen del concepto
                                            <span class="item-summary__currency">
                                                {{ getItemInvoiceCurrency(item) }}
                                            </span>
                                        </div>

                                        <div
                                            v-if="requiresCurrencyConversion(getBillFromItem(item)) && !hasValidExchangeRate(item)"
                                            class="item-summary__warning"
                                        >
                                            <i class="fas fa-exclamation-triangle mr-2"></i>
                                            Ingresa el tipo de cambio para calcular el resumen en MXN.
                                        </div>

                                        <template v-else>
                                            <div class="item-summary__row">
                                                <span>Importe</span>
                                                <strong>
                                                    {{ calcularTotales(item).montoBase | toCurrency }}
                                                    {{ getItemInvoiceCurrency(item) }}
                                                </strong>
                                            </div>

                                            <div
                                                class="item-summary__row item-summary__row--discount"
                                                v-if="item.discount > 0"
                                            >
                                                <span>Descuento</span>
                                                <strong>
                                                    - {{ calcularTotales(item).descuento | toCurrency }}
                                                    {{ getItemInvoiceCurrency(item) }}
                                                </strong>
                                            </div>

                                            <div class="item-summary__row item-summary__row--subtotal">
                                                <span>Subtotal después de descuento</span>
                                                <strong>
                                                    {{ calcularTotales(item).subtotal | toCurrency }}
                                                    {{ getItemInvoiceCurrency(item) }}
                                                </strong>
                                            </div>

                                            <div class="item-summary__row" v-if="item.ieps">
                                                <span>IEPS (8%)</span>
                                                <strong>
                                                    + {{ calcularTotales(item).ieps | toCurrency }}
                                                    {{ getItemInvoiceCurrency(item) }}
                                                </strong>
                                            </div>

                                            <div class="item-summary__row" v-if="item.iva">
                                                <span>IVA (16%)</span>
                                                <strong>
                                                    + {{ calcularTotales(item).iva | toCurrency }}
                                                    {{ getItemInvoiceCurrency(item) }}
                                                </strong>
                                            </div>

                                            <div
                                                class="item-summary__row item-summary__row--retention"
                                                v-if="item.isr_ret"
                                            >
                                                <span>ISR retenido (10%)</span>
                                                <strong>
                                                    - {{ calcularTotales(item).isrRet | toCurrency }}
                                                    {{ getItemInvoiceCurrency(item) }}
                                                </strong>
                                            </div>

                                            <div
                                                class="item-summary__row item-summary__row--retention"
                                                v-if="item.iva_ret"
                                            >
                                                <span>IVA retenido (10.6667%)</span>
                                                <strong>
                                                    - {{ calcularTotales(item).ivaRet | toCurrency }}
                                                    {{ getItemInvoiceCurrency(item) }}
                                                </strong>
                                            </div>

                                            <div class="item-summary__total">
                                                <span>Total</span>
                                                <strong>
                                                    {{ calcularTotales(item).total | toCurrency }}
                                                    {{ getItemInvoiceCurrency(item) }}
                                                </strong>
                                            </div>
                                        </template>
                                    </div>
                                </div>
                            </div>

                            <button
                                v-if="prop_edit"
                                type="button"
                                class="btn billing-btn billing-btn--add-item"
                                @click="addItem()"
                            >
                                <i class="fas fa-plus mr-2"></i>
                                Agregar concepto
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Footer -->
                <div class="billing-modal__footer">
                    <div class="billing-modal__footer-info">
                        <i class="fas fa-info-circle mr-2"></i>
                        Los importes base permanecen en la moneda del contrato. La conversión solo aplica de USD a MXN.
                    </div>

                    <div class="billing-modal__actions">
                        <button
                            type="button"
                            class="btn billing-btn billing-btn--secondary"
                            @click="closedModal()"
                        >
                            <i class="fas fa-arrow-left mr-2"></i>
                            Regresar
                        </button>

                        <button
                            :disabled="send"
                            type="button"
                            class="btn billing-btn billing-btn--primary"
                            @click="triggerSave()"
                        >
                            <span v-if="send">
                                <i class="fas fa-circle-notch fa-spin mr-2"></i>
                                Facturando...
                            </span>
                            <span v-else>
                                <i class="fas fa-file-invoice-dollar mr-2"></i>
                                Facturar
                            </span>
                        </button>
                    </div>
                </div>
            </template>
        </div>
    </modal>
</template>

<style scoped>
.billing-modal {
    --primary: #5672a5;
    --primary-dark: #304d7d;
    --primary-soft: #eef3fb;
    --success: #269b74;
    --success-soft: #eaf8f3;
    --warning: #d99b24;
    --warning-soft: #fff8e8;
    --danger: #d84d5d;
    --danger-soft: #fff0f2;
    --border: #dfe5ee;
    --surface: #ffffff;
    --background: #f5f7fb;
    --text: #1f2937;
    --muted: #6b7280;

    background: var(--background);
    color: var(--text);
    min-height: 400px;
}

/* HEADER */
.billing-modal__header {
    align-items: center;
    background: linear-gradient(135deg, var(--primary-dark), var(--primary));
    color: #ffffff;
    display: flex;
    gap: 18px;
    justify-content: space-between;
    padding: 18px 22px;
}

.billing-modal__title-group {
    align-items: center;
    display: flex;
    gap: 14px;
    min-width: 0;
}

.billing-modal__icon {
    align-items: center;
    background: rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    display: flex;
    flex: 0 0 46px;
    font-size: 22px;
    height: 46px;
    justify-content: center;
    width: 46px;
}

.billing-modal__title {
    color: #ffffff;
    font-size: 18px;
    font-weight: 700;
    margin: 0;
}

.billing-modal__subtitle {
    color: rgba(255, 255, 255, 0.78);
    font-size: 12px;
    line-height: 1.45;
    margin: 3px 0 0;
}

.billing-modal__close {
    align-items: center;
    background: rgba(255, 255, 255, 0.12);
    border: 0;
    border-radius: 50%;
    color: #ffffff;
    cursor: pointer;
    display: flex;
    flex: 0 0 36px;
    height: 36px;
    justify-content: center;
    transition: 0.2s ease;
    width: 36px;
}

.billing-modal__close:hover {
    background: rgba(255, 255, 255, 0.24);
    color: #ffffff;
    transform: rotate(90deg);
}

/* BODY */
.billing-modal__body {
    max-height: calc(100vh - 185px);
    overflow-y: auto;
    padding: 22px;
}

.billing-section-header {
    align-items: center;
    display: flex;
    gap: 12px;
    justify-content: space-between;
    margin-bottom: 14px;
}

.billing-section-header__left {
    align-items: flex-start;
    display: flex;
    gap: 12px;
    min-width: 0;
}

.billing-section-header__number {
    align-items: center;
    background: var(--primary-soft);
    border-radius: 50%;
    color: var(--primary-dark);
    display: flex;
    flex: 0 0 30px;
    font-size: 13px;
    font-weight: 700;
    height: 30px;
    justify-content: center;
    width: 30px;
}

.billing-section-header__title {
    color: var(--text);
    font-size: 15px;
    font-weight: 700;
    margin: 1px 0 3px;
}

.billing-section-header__text {
    color: var(--muted);
    font-size: 12px;
    line-height: 1.4;
    margin: 0;
}

.billing-count-badge {
    align-items: center;
    background: var(--primary-soft);
    border: 1px solid #dbe5f3;
    border-radius: 999px;
    color: var(--primary-dark);
    display: inline-flex;
    flex: 0 0 auto;
    font-size: 11px;
    font-weight: 700;
    justify-content: center;
    min-width: 29px;
    padding: 5px 9px;
}

/* FACTURAS Y CONCEPTOS */
.invoice-list {
    display: grid;
    gap: 12px;
    margin-bottom: 12px;
}

.invoice-card,
.item-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    box-shadow: 0 3px 10px rgba(15, 23, 42, 0.04);
    overflow: hidden;
}

.invoice-card__header,
.item-card__header {
    align-items: center;
    background: #fafbfd;
    border-bottom: 1px solid var(--border);
    display: flex;
    gap: 11px;
    justify-content: space-between;
    padding: 13px 15px;
}

.invoice-card__header {
    justify-content: flex-start;
}

.invoice-card__number,
.item-card__index {
    align-items: center;
    background: var(--primary-soft);
    border-radius: 8px;
    color: var(--primary-dark);
    display: flex;
    flex: 0 0 32px;
    font-size: 12px;
    font-weight: 700;
    height: 32px;
    justify-content: center;
    width: 32px;
}

.invoice-card__heading,
.item-card__title > div {
    min-width: 0;
}

.invoice-card__heading strong,
.invoice-card__heading small {
    display: block;
}

.invoice-card__heading strong {
    color: var(--text);
    font-size: 12px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.invoice-card__heading small {
    color: var(--muted);
    font-size: 10px;
    margin-top: 2px;
}

.invoice-card__body,
.item-card__body {
    padding: 16px;
}

.invoice-card__body .form-group,
.item-card__body .form-group {
    margin-bottom: 14px;
}

.invoice-card__body .form-group:last-child,
.item-card__body .form-group:last-child {
    margin-bottom: 0;
}

.item-card {
    margin-bottom: 14px;
}

.item-card__title {
    align-items: center;
    display: flex;
    gap: 10px;
    min-width: 0;
}

.item-card__label {
    color: var(--muted);
    display: block;
    font-size: 10px;
    margin-bottom: 2px;
    text-transform: uppercase;
}

.item-card__title strong {
    color: var(--text);
    display: block;
    font-size: 12px;
    line-height: 1.35;
}

.item-card__amount {
    flex: 0 0 auto;
    text-align: right;
}

.item-card__amount span,
.item-card__amount strong,
.item-card__amount small {
    display: block;
}

.item-card__amount span {
    color: var(--muted);
    font-size: 10px;
}

.item-card__amount strong {
    color: var(--primary-dark);
    font-size: 14px;
    margin-top: 2px;
}

.billing-conversion {
    color: var(--success);
    font-size: 10px;
    font-weight: 700;
    margin-top: 3px;
}

/* CAMPOS */
.billing-label {
    color: #374151;
    display: block;
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 6px;
}

.billing-control.form-control,
.form-control.billing-control {
    border-color: #d5dce6;
    border-radius: 7px;
    font-size: 12px;
    min-height: 38px;
}

.billing-control.form-control:focus,
.form-control.billing-control:focus {
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(86, 114, 165, 0.12);
}

.billing-help {
    color: var(--muted);
    display: block;
    font-size: 10px;
    line-height: 1.45;
    margin-top: 5px;
}

.billing-error {
    color: var(--danger);
    font-size: 11px;
    margin-top: 5px;
}

.billing-readonly-value {
    align-items: center;
    background: #f8fafc;
    border: 1px solid #d5dce6;
    border-radius: 7px;
    color: var(--text);
    display: flex;
    font-size: 12px;
    font-weight: 700;
    min-height: 38px;
    padding: 7px 10px;
}

.billing-readonly-value--currency {
    align-items: flex-start;
    flex-direction: column;
    justify-content: center;
}

.billing-readonly-value--currency small {
    color: var(--success);
    display: block;
    font-size: 10px;
    font-weight: 700;
    margin-top: 2px;
}

/* TIPO DE CAMBIO */
.exchange-panel {
    background: var(--warning-soft);
    border: 1px solid #f1d89d;
    border-radius: 9px;
    padding: 12px;
}

.exchange-panel__title {
    align-items: center;
    color: #8c6515;
    display: flex;
    font-size: 11px;
    font-weight: 700;
    gap: 7px;
    margin-bottom: 10px;
}

.conversion-preview {
    align-items: center;
    background: var(--success-soft);
    border: 1px solid #ccebdd;
    border-radius: 10px;
    display: flex;
    gap: 11px;
    margin: 2px 0 14px;
    padding: 11px 13px;
}

.conversion-preview__icon {
    align-items: center;
    background: #ffffff;
    border-radius: 8px;
    color: var(--success);
    display: flex;
    flex: 0 0 34px;
    height: 34px;
    justify-content: center;
    width: 34px;
}

.conversion-preview__content strong,
.conversion-preview__content span {
    display: block;
}

.conversion-preview__content strong {
    color: #1f6f55;
    font-size: 11px;
    margin-bottom: 2px;
}

.conversion-preview__content span {
    color: #3f5f54;
    font-size: 11px;
}

.conversion-preview__pending {
    color: #8c6515 !important;
}

/* IMPUESTOS */
.tax-panel {
    background: #f8fafc;
    border: 1px solid var(--border);
    border-radius: 10px;
    margin-top: 15px;
    padding: 14px;
}

.tax-panel__header {
    margin-bottom: 11px;
}

.tax-panel__header strong,
.tax-panel__header small {
    display: block;
}

.tax-panel__header strong {
    color: var(--text);
    font-size: 12px;
}

.tax-panel__header small {
    color: var(--muted);
    font-size: 10px;
    margin-top: 2px;
}

.tax-options {
    display: grid;
    gap: 9px;
    grid-template-columns: repeat(4, minmax(0, 1fr));
}

.tax-option {
    align-items: center;
    background: #ffffff;
    border: 1px solid var(--border);
    border-radius: 9px;
    cursor: pointer;
    display: flex;
    gap: 9px;
    margin: 0;
    min-height: 56px;
    padding: 10px;
    position: relative;
    transition: 0.2s ease;
}

.tax-option:hover {
    border-color: var(--primary);
    box-shadow: 0 4px 12px rgba(48, 77, 125, 0.08);
    transform: translateY(-1px);
}

.tax-option--active {
    background: var(--primary-soft);
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(86, 114, 165, 0.08);
}

.tax-option input {
    height: 1px;
    opacity: 0;
    position: absolute;
    width: 1px;
}

.tax-option__check {
    align-items: center;
    background: #f3f5f8;
    border-radius: 7px;
    color: transparent;
    display: flex;
    flex: 0 0 30px;
    font-size: 11px;
    height: 30px;
    justify-content: center;
    transition: 0.2s ease;
    width: 30px;
}

.tax-option--active .tax-option__check {
    background: #ffffff;
    color: var(--primary);
}

.tax-option strong,
.tax-option small {
    display: block;
}

.tax-option strong {
    color: var(--text);
    font-size: 11px;
}

.tax-option small {
    color: var(--muted);
    font-size: 9px;
    margin-top: 1px;
}

/* RESUMEN */
.item-summary {
    border: 1px solid var(--border);
    border-radius: 10px;
    margin-top: 14px;
    overflow: hidden;
}

.item-summary__title {
    align-items: center;
    background: var(--primary-dark);
    color: #ffffff;
    display: flex;
    font-size: 11px;
    font-weight: 700;
    padding: 10px 13px;
}

.item-summary__currency {
    background: rgba(255, 255, 255, 0.14);
    border-radius: 999px;
    font-size: 9px;
    margin-left: auto;
    padding: 3px 7px;
}

.item-summary__warning {
    background: var(--warning-soft);
    color: #8c6515;
    font-size: 11px;
    padding: 12px 13px;
}

.item-summary__row,
.item-summary__total {
    align-items: center;
    border-bottom: 1px solid #edf0f4;
    display: flex;
    font-size: 11px;
    justify-content: space-between;
    padding: 9px 13px;
}

.item-summary__row span {
    color: var(--muted);
}

.item-summary__row strong {
    color: var(--text);
    font-size: 11px;
    text-align: right;
}

.item-summary__row--discount,
.item-summary__row--retention {
    background: #fffafb;
}

.item-summary__row--discount strong,
.item-summary__row--retention strong {
    color: var(--danger);
}

.item-summary__row--subtotal {
    background: #fafbfd;
}

.item-summary__total {
    background: var(--primary-soft);
    border-bottom: 0;
    color: var(--primary-dark);
    padding: 11px 13px;
}

.item-summary__total span {
    font-weight: 600;
}

.item-summary__total strong {
    color: var(--primary-dark);
    font-size: 14px;
    text-align: right;
}

/* BOTONES */
.billing-btn {
    border-radius: 7px;
    font-size: 12px;
    font-weight: 600;
    min-height: 38px;
    padding: 8px 14px;
}

.billing-btn--add,
.billing-btn--add-item {
    background: var(--primary-soft);
    border: 1px solid #d8e2f1;
    color: var(--primary-dark);
}

.billing-btn--add:hover,
.billing-btn--add-item:hover {
    background: var(--primary);
    border-color: var(--primary);
    color: #ffffff;
}

.billing-btn--add-item {
    width: 100%;
}

.billing-btn--secondary {
    background: #ffffff;
    border: 1px solid var(--border);
    color: #4b5563;
    min-width: 120px;
}

.billing-btn--secondary:hover {
    background: #f3f5f8;
    color: var(--text);
}

.billing-btn--primary {
    background: var(--success);
    border: 1px solid var(--success);
    color: #ffffff;
    min-width: 160px;
}

.billing-btn--primary:hover,
.billing-btn--primary:focus {
    background: #218562;
    border-color: #218562;
    color: #ffffff;
}

.billing-btn--primary:disabled {
    cursor: not-allowed;
    opacity: 0.65;
}

/* ESTADOS */
.billing-loading,
.billing-empty-state {
    align-items: center;
    color: var(--muted);
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 220px;
    padding: 35px 20px;
    text-align: center;
}

.billing-loading {
    background: var(--background);
}

.billing-loading__spinner,
.billing-empty-state__icon {
    align-items: center;
    background: var(--primary-soft);
    border-radius: 50%;
    color: var(--primary);
    display: flex;
    font-size: 22px;
    height: 48px;
    justify-content: center;
    margin-bottom: 11px;
    width: 48px;
}

.billing-loading h5,
.billing-empty-state h5 {
    color: var(--text);
    font-size: 14px;
    font-weight: 700;
}

.billing-loading p,
.billing-empty-state p {
    font-size: 11px;
}

/* FOOTER */
.billing-modal__footer {
    align-items: center;
    background: #ffffff;
    border-top: 1px solid var(--border);
    display: flex;
    gap: 12px;
    justify-content: space-between;
    padding: 14px 22px;
}

.billing-modal__footer-info {
    align-items: center;
    color: var(--muted);
    display: flex;
    font-size: 10px;
    line-height: 1.4;
}

.billing-modal__footer-info i {
    color: var(--primary);
}

.billing-modal__actions {
    display: flex;
    gap: 10px;
}

@media (max-width: 1200px) {
    .tax-options {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 991px) {
    .billing-modal__body {
        padding: 16px;
    }

    .billing-section-header--items {
        margin-top: 2px;
    }
}

@media (max-width: 768px) {
    .billing-modal__header {
        padding: 15px;
    }

    .billing-modal__subtitle {
        display: none;
    }

    .billing-modal__body {
        padding: 14px;
    }

    .tax-options {
        grid-template-columns: 1fr;
    }

    .item-card__header,
    .billing-modal__footer {
        align-items: stretch;
        flex-direction: column;
    }

    .item-card__amount {
        margin-left: 42px;
        text-align: left;
    }

    .billing-modal__footer-info {
        display: none;
    }

    .billing-modal__actions {
        flex-direction: column-reverse;
        width: 100%;
    }

    .billing-modal__actions .billing-btn {
        width: 100%;
    }
}
</style>
